import { Injectable, inject, PLATFORM_ID, Inject } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of, from, throwError } from "rxjs";
import { environment } from "@environments/environment";
import { ProfileDetails } from "../models/profile.interface";
import { AuthService } from "./auth.service";
import { delay, map, switchMap, catchError, tap } from "rxjs/operators";
import { ResumeAnalyzerService } from "./resume-analyzer.service";
import { SupabaseService } from "./supabase.service";
import { z } from "zod";
// pdfjs-dist is dynamically imported to avoid SSR issues (DOMMatrix not defined in Node.js)

// Add PDF.js type declaration
declare global {
  interface Window {
    pdfjsLib: any;
    importScripts?: (url: string) => Promise<void>;
  }
}

// Type alias for casting if needed frequently
type EnvironmentType = typeof environment;

// ============================================
// ZOD VALIDATION SCHEMAS
// ============================================

const WorkExperienceSchema = z.object({
  company: z.string().default(""),
  title: z.string().default(""),
  startDate: z.string().default(""),
  endDate: z.string().default(""),
  duration: z.string().default(""),
  location: z.string().default("Not Specified"),
  description: z.string().optional().default(""),
  responsibilities: z.array(z.string()).default([]),
  achievements: z.array(z.string()).default([]),
  technologies: z.array(z.string()).default([]),
});

const EducationSchema = z.object({
  degree: z.string().default(""),
  degreeLevel: z.string().default(""),
  institution: z.string().default(""),
  year: z.union([z.string(), z.number()]).transform(val => String(val)).default(""),
  location: z.string().default("Not Specified"),
  description: z.string().optional().default(""),
});

const ProfileDataSchema = z.object({
  location: z.string().default("Not Specified"),
  seniority: z.string().default(""),
  yearsOfExperience: z.union([z.string(), z.number()]).transform(val => 
    typeof val === 'number' ? String(val) : val
  ).default(""),
  targetRoles: z.array(z.string()).default([]),
  portfolioUrl: z.string().default(""),
  linkedinUrl: z.string().default(""),
  githubUrl: z.string().default(""),
  dribbbleUrl: z.string().default(""),
  behanceUrl: z.string().default(""),
  twitterUrl: z.string().default(""),
  instagramUrl: z.string().default(""),
  mediumUrl: z.string().default(""),
  profileEmail: z.string().default(""),
  notableClients: z.array(z.string()).default([]),
  workExperience: z.array(WorkExperienceSchema).default([]),
  education: z.array(EducationSchema).default([]),
  educationLevel: z.string().default(""),
  certifications: z.array(z.string()).default([]),
  languages: z.array(z.string()).default([]),
  summary: z.string().default(""),
  industries: z.array(z.string()).default([]),
  preferredWorkMode: z.string().default("Not Specified"),
  keyProjects: z.array(z.string()).default([]),
  keyTools: z.array(z.string()).default([]),
  skills: z.array(z.string()).default([]),
});

/**
 * Service for processing resumes, including:
 * - PDF text extraction
 * - Resume analysis
 * - Profile data generation
 * - Resume storage management
 */
@Injectable({
  providedIn: "root",
})
export class ResumeProcessingService {
  private supabase = inject(SupabaseService);
  private http = inject(HttpClient);
  private readonly API_URL = (environment as EnvironmentType).apiUrl || "";
  private pdfjsLib: any = null;
  private isBrowser: boolean;
  private readonly pdfWorkerSrc = "/assets/pdfjs/pdf.worker.min.mjs";

  constructor(
    private authService: AuthService,
    private resumeAnalyzerService: ResumeAnalyzerService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Dynamically load pdfjs-dist to avoid SSR issues
   */
  private async loadPdfJs(): Promise<any> {
    if (this.pdfjsLib) return this.pdfjsLib;
    
    if (!this.isBrowser) {
      throw new Error("PDF processing is only available in browser");
    }

    const pdfjsModule = await import("pdfjs-dist");
    this.pdfjsLib = pdfjsModule;
    return this.pdfjsLib;
  }

  /**
   * Verify that the storage bucket exists
   */
  private async ensureStorageBucket(): Promise<void> {
    try {
      const { data: buckets, error } = await this.supabase
        .getClient()
        .storage.listBuckets();

      if (error) throw error;

      const resumesBucketExists = buckets?.some((b) => b.name === "resumes");
      if (!resumesBucketExists) {
        throw new Error(
          'Storage bucket "resumes" not found. Please create it in your Supabase dashboard.'
        );
      }
    } catch (error) {
      console.error("Storage bucket check failed:", error);
      throw new Error(
        "Failed to verify storage configuration. Please check your Supabase setup."
      );
    }
  }

  /**
   * Upload a resume to temporary storage without analysis
   */
  uploadResumeTemp(
    file: File,
    portfolioUrl?: string
  ): Observable<{
    tempResumeId: string;
    filePath: string;
    portfolioUrl?: string;
  }> {
    // Generate a temporary ID for the resume
    const tempId = crypto.randomUUID();
    const filePath = `temp/${tempId}/${encodeURIComponent(file.name)}`;

    // Upload file to temporary storage
    return from(
      this.supabase.getClient().storage.from("resumes").upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      })
    ).pipe(
      catchError((error) => {
        console.error("Resume upload failed:", error);
        return throwError(() => ({
          message: "Failed to upload resume. Please try again later.",
          details: error,
        }));
      }),
      map((uploadResult) => {
        if (uploadResult.error) {
          throw new Error(uploadResult.error.message);
        }

        return {
          tempResumeId: tempId,
          filePath,
          portfolioUrl,
        };
      })
    );
  }

  /**
   * Extract text from PDF file
   *
   * This method uses a cascading approach:
   * 1. First downloads the PDF from storage
   * 2. Attempts direct text extraction from the PDF (this happens outside this service)
   * 3. Uses the extracted text with gpt-4o-mini via analyzeResumeText() (cheaper approach)
   * 4. If direct text extraction fails, falls back to vision-based AI
   *    (ResumeAnalyzerService.analyzeResumeFile) using the more expensive gpt-4o model
   * 5. If all approaches fail, falls back to a generic response
   */
  private async extractTextFromPDF(filePath: string): Promise<string> {
    try {
      // Download the file from Supabase storage
      const { data, error } = await this.supabase
        .getClient()
        .storage.from("resumes")
        .download(filePath);

      if (error) {
        throw new Error(
          "Failed to download file for text extraction: " + error.message
        );
      }

      // Extract filename from path or use default
      const fileName = decodeURIComponent(filePath.split('/').pop() || 'Resume.pdf');
      
      // Create a File object from the blob
      const file = new File([data], fileName, { type: "application/pdf" });

      try {
        // STEP 1: First attempt to extract text directly from the PDF
        try {
          // First try to extract text directly from the PDF
          const textContent = await this.extractTextDirectlyFromPDF(file);

          if (textContent && textContent.length > 100) {
            // If we got enough text content, return it for later analysis
            return textContent.trim();
          } else {
            console.warn(
              "Text extraction yielded insufficient content, falling back to vision API"
            );
          }
        } catch (textError) {
          console.warn(
            "Text extraction failed, falling back to vision API",
            textError
          );
        }

        // STEP 2: If text extraction failed, fall back to vision-based OCR
        const visionPrompt = `Please extract all the text content from this PDF resume. Focus on professional experience, skills, education, and contact information. Return only the extracted text content without any additional commentary.`;

        // Use the more expensive model with vision capabilities as a fallback
        const visionCompletion =
          await this.resumeAnalyzerService.analyzeResumeFile(
            file,
            visionPrompt
          );

        return visionCompletion.trim();
      } catch (aiError: any) {
        console.error("Error with OpenAI processing:", aiError);

        // Create a comprehensive error check for any PDF-related or model-related issues
        const errorMessage =
          (aiError.error?.error?.message || "") +
          (aiError.message || "") +
          JSON.stringify(aiError.error || {});

        // Check for any of the common error messages
        if (
          errorMessage.includes("PDF") ||
          errorMessage.includes("MIME type") ||
          errorMessage.includes("file format") ||
          errorMessage.includes("processing limitations") ||
          errorMessage.includes("Forbidden") ||
          errorMessage.includes("403") ||
          aiError.status === 403
        ) {
          console.error(
            "PDF processing failed due to API limitations or file format issues"
          );
          throw new Error(
            "Unable to process this PDF file. Please try uploading a different format or contact support for assistance."
          );
        }

        // Re-throw the original error if not PDF-specific
        throw aiError;
      }
    } catch (error) {
      console.error("Error extracting text from PDF:", error);
      throw new Error(
        "Failed to extract text from PDF file: " +
          (error instanceof Error ? error.message : String(error))
      );
    }
  }

  /**
   * Public wrapper to extract text directly from a PDF file.
   * Uses pdfjs-dist npm package for reliable extraction.
   */
  public async getTextFromPdfFile(file: File): Promise<string> {
    try {
      return await this.extractTextDirectlyFromPDF(file);
    } catch (error) {
      console.error("Error during getTextFromPdfFile:", error);
      throw new Error(
        "Could not extract text from the provided PDF file. It might be image-based or corrupted."
      );
    }
  }

  /**
   * Extract text directly from a PDF file using pdfjs-dist npm package.
   * This is more reliable than CDN loading approach.
   */
  private async extractTextDirectlyFromPDF(file: File): Promise<string> {
    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await this.fileToArrayBuffer(file);
      
      // Dynamically import pdfjs-dist to avoid SSR issues
      const pdfjsLib = await this.loadPdfJs();
      
      // Configure worker
      pdfjsLib.GlobalWorkerOptions.workerSrc = this.pdfWorkerSrc;
      
      // Load PDF document
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let extractedText = "";
      
      // Extract text from each page
      for (let i = 1; i <= pdf.numPages; i++) {
        try {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const textItems = textContent.items;
          
          // Concatenate text items with proper spacing
          const pageText = textItems
            .map((item: any) => item.str)
            .join(" ");
          
          extractedText += pageText + "\n\n";
        } catch (pageError) {
          console.error(`Error extracting text from page ${i}:`, pageError);
          // Continue with next page
        }
      }
      
      if (extractedText.trim().length === 0) {
        console.warn("No text extracted from PDF, document may be scanned or image-based");
        throw new Error("Could not extract text from PDF");
      }

      return extractedText;
      
    } catch (error) {
      console.error("PDF extraction failed:", error);
      throw error;
    }
  }

  /**
   * Convert File to ArrayBuffer
   */
  private fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Detect if text is primarily non-English and translate if needed
   */
  private async translateIfNeeded(text: string): Promise<string> {
    // Quick heuristic: check for common English words
    const englishIndicators = /\b(the|and|or|is|are|was|were|have|has|been|will|would|could|should|experience|work|education|skills|company|position|responsibilities)\b/gi;
    const matches = text.match(englishIndicators) || [];
    
    // If we find enough English words, assume it's English
    const wordCount = text.split(/\s+/).length;
    const englishRatio = matches.length / Math.max(wordCount, 1);
    
    if (englishRatio > 0.05) {
      // Likely English, return as-is
      return text;
    }
    
    // Text appears to be non-English, translate it
    console.log("Detected non-English resume, translating...");
    
    const translationPrompt = `Translate the following resume text to English. Preserve all formatting, names, company names, and technical terms. Only translate the descriptive text. Return only the translated text without any additional commentary.

Resume text:
${text}`;

    try {
      const translatedText = await this.resumeAnalyzerService.analyzeResume(translationPrompt);
      console.log("Translation complete");
      return translatedText;
    } catch (error) {
      console.warn("Translation failed, proceeding with original text:", error);
      return text;
    }
  }

  /**
   * Analyze resume text using OpenAI
   *
   * This method analyzes the extracted text to create a detailed profile with comprehensive
   * information for job matching. It uses the more cost-effective gpt-4o-mini model for text.
   */
  public async analyzeResumeText(text: string): Promise<ProfileDetails> {
    try {
      // Translate if non-English
      const processedText = await this.translateIfNeeded(text);
      
      // Comprehensive text-based analysis for detailed job matching
      const prompt = `Analyze this resume comprehensively for job matching purposes. Extract the following information in JSON format:

{
  "location": "candidate's location - see LOCATION INFERENCE rules below",
  "seniority": "Junior, Mid-level, Senior, Lead, or Principal - see SENIORITY rules below",
  "yearsOfExperience": "total years of professional experience as a number",
  "targetRoles": ["relevant job titles - see TARGET ROLES rules below"],
  "portfolioUrl": "actual URL of personal website/portfolio (the link destination), NOT the link label - leave empty if only a label like 'PORTFOLIO' or 'Website' appears with no URL",
  "linkedinUrl": "full LinkedIn profile URL if present (e.g. https://linkedin.com/in/username)",
  "githubUrl": "GitHub profile URL if present (e.g. https://github.com/username)",
  "dribbbleUrl": "Dribbble profile URL if present (e.g. https://dribbble.com/username)",
  "behanceUrl": "Behance profile URL if present (e.g. https://behance.net/username)",
  "twitterUrl": "Twitter/X profile URL if present (e.g. https://x.com/username or twitter.com/username)",
  "instagramUrl": "Instagram profile URL if present (e.g. https://instagram.com/username)",
  "mediumUrl": "Medium profile URL if present (e.g. https://medium.com/@username)",
  "profileEmail": "contact email if clearly stated on the resume (leave empty if not found)",
  "notableClients": ["agency/freelance clients like Google, World Bank, Intel - if mentioned"],
  
  "workExperience": [
    {
      "company": "company name",
      "title": "job title",
      "startDate": "MM/YYYY",
      "endDate": "MM/YYYY or 'Present'",
      "duration": "calculated duration e.g. '2 years 4 months' or '9 months' - calculate from start/end dates",
      "location": "job location if available",
      "description": "verbatim paragraph OR empty when bullets used - see WORK EXPERIENCE DESCRIPTION rule",
      "responsibilities": ["ONE string per bullet when resume has bullets; empty when resume has paragraph"],
      "achievements": ["ONE string per achievement bullet; empty when none - see ACHIEVEMENTS rules"],
      "technologies": ["ALL technologies, tools, frameworks, practices used - see TECHNOLOGIES rules"]
    }
  ],
  
  "education": [
    {
      "degree": "degree name",
      "degreeLevel": "High School, Associate, Bachelor's, Master's, PhD, or Certificate",
      "institution": "institution name",
      "year": "graduation year",
      "location": "location if available",
      "description": "optional: verbatim education text - see EDUCATION DESCRIPTION rule"
    }
  ],
  
  "educationLevel": "highest degree level: Bachelor's, Master's, PhD, etc.",
  "certifications": ["certifications with years if available"],
  "languages": ["languages with proficiency - see LANGUAGE INFERENCE rules"],
  "summary": "2-3 sentence professional summary - MUST be in first person (I am... I have...) as if the candidate wrote it, never third person",
  "industries": ["industries with demonstrated experience"],
  "preferredWorkMode": "Remote, Hybrid, On-site, or Not Specified",
  "keyProjects": ["notable projects with brief descriptions"],
  "keyTools": ["primary tools used frequently - see KEY TOOLS rules"],
  "skills": ["skills list - see SKILLS rules below"]
}

=== CRITICAL EXTRACTION RULES ===

BULLET POINTS VS PARAGRAPHS (WORK EXPERIENCE) - CRITICAL:
- DETECT the format used in the resume for each role by looking for EXPLICIT bullet markers:
  * BULLETS: lines starting with • or − or - or * or ◦ or ▪ or numbers (1. 2. 3.). If you see ANY of these markers, this is DEFINITELY a bulleted list.
  * PARAGRAPH: continuous text with NO bullet markers. This is the default assumption unless you see explicit markers.
- When the resume has EXPLICIT BULLET MARKERS (•, -, *, etc.) for a role:
  * You MUST output each bullet as a SEPARATE string in "responsibilities" and/or "achievements" (one array element per bullet). Do NOT merge multiple bullets into one string.
  * Strip the bullet markers from the text (remove the •, -, *, etc.) but keep the rest verbatim.
  * Leave "description" EMPTY when using bullets. Do NOT put the same text in both description and responsibilities.
  * Example: if the resume has "• Led design system" and "• Shipped 3 products" then responsibilities must be ["Led design system", "Shipped 3 products"], not one string.
- When the resume has NO BULLET MARKERS (just continuous text) for a role:
  * Put the full text verbatim in "description". Leave "responsibilities" and "achievements" empty.
  * Even if the paragraph contains multiple sentences starting with action verbs, if there are NO bullet markers, treat it as a paragraph.
  * Do NOT split continuous text into separate array elements unless explicit bullet markers are present.
- Preserve the candidate's exact wording in both cases; do not paraphrase.
- CRITICAL: You cannot put the same content in BOTH description AND responsibilities. Choose one format based on the rules above. If you populate responsibilities, description must be empty.
- CRITICAL: The presence or absence of bullet markers (•, -, *, ◦, ▪, 1., 2., etc.) is the ONLY factor that determines bullets vs paragraph. Do NOT use sentence structure or action verbs as indicators.

SUMMARY (ABOUT) - FIRST PERSON ONLY:
- The "summary" field is the profile "About" text. It MUST be written in first person, as if the candidate wrote it.
- Use "I" (e.g. "I am a...", "I have...", "I specialize in..."). Do NOT use third person (e.g. "[Name] is a...", "He has...", "She has...").
- If the resume has a third-person summary or bio, rewrite it in first person. Keep the same facts and length (2-3 sentences).

TRANSLATION NOTE:
If this resume was translated from another language, preserve proper nouns (names, companies, institutions) in their original form where possible.

LOCATION INFERENCE:
- If location is explicitly stated, use it
- If phone number has country code, infer country (e.g., +351 = Portugal, +44 = UK, +1 = USA/Canada, +49 = Germany, +33 = France, +34 = Spain, +39 = Italy, +31 = Netherlands, +46 = Sweden, +47 = Norway, +45 = Denmark, +358 = Finland, +48 = Poland, +420 = Czech Republic, +36 = Hungary, +91 = India, +86 = China, +81 = Japan, +82 = South Korea, +61 = Australia, +64 = New Zealand, +55 = Brazil, +52 = Mexico, +54 = Argentina)
- If schools/universities are location-specific and well-known, use that country
- If company locations are mentioned, use the most recent
- Only use "Not Specified" if truly no location clues exist

TARGET ROLES - RULES:
- Include ALL job titles the candidate has ACTUALLY HELD (exact titles from resume)
- Include close variations and natural progressions (e.g., Senior → Lead, Lead → Principal, Designer → Head of Design)
- Include hybrid/unique titles if they describe themselves that way (e.g., "Product Design Engineer", "Full-stack Designer")
- If they did freelance work, include "Freelance [Role]" as a valid target
- IMPORTANT: If they explicitly did full-stack development work (both front-end AND back-end), you MUST include "Full-stack Developer" as a target role
- DO NOT include "Founder" or "Co-founder" - these are not roles people apply for on job boards
- DO NOT add unrelated roles (e.g., don't add "Product Manager" unless they actually held a PM title)
- Aim for 4-6 varied but relevant target roles based on their actual experience

LANGUAGE INFERENCE - CONSERVATIVE APPROACH:
- The resume language itself indicates proficiency (English resume = English proficiency)
- Phone country codes suggest native language: +351 = Portuguese, +49 = German, +33 = French, +34 = Spanish, +39 = Italian, +31 = Dutch, +46 = Swedish, +47 = Norwegian, +45 = Danish, +358 = Finnish, +48 = Polish, +91 = Hindi, +86 = Mandarin, +81 = Japanese, +82 = Korean, +55 = Portuguese, +52 = Spanish
- Schools in specific non-English countries suggest that language (e.g., university in Germany = German)
- DO NOT infer language from names - names are not reliable indicators
- Only include languages you have strong evidence for
- Format: "Language - Proficiency" (Native, Fluent, Conversational, Basic)

KEY TOOLS - Primary tools used across multiple roles:
- Design tools: Figma, Sketch, Adobe XD, Photoshop, Illustrator, etc.
- Development frameworks: Angular, React, Vue, TypeScript, Node.js, etc.
- Other tools: Jira, Notion, Miro, Slack, etc.
- Only include tools EXPLICITLY mentioned in the resume

PROFILE LINKS - EXTRACT ACTUAL URLs (CRITICAL):
- Extract the actual URL (the link destination/href) for each profile or portfolio link. Look in header, contact section, footer, or anywhere in the resume.
- You MUST extract the URL that a link points to, NOT the clickable text or label. Example: if the resume shows "PORTFOLIO" or "Website" as clickable text linking to https://johndoe.com, output portfolioUrl as "https://johndoe.com", NOT "PORTFOLIO" or "Website".
- Never put link labels in URL fields. Values like "PORTFOLIO", "Website", "LinkedIn", "Link", "GitHub", "View portfolio" are LABELS only - if you cannot find the actual URL they point to, leave that field empty.
- "linkedinUrl": full LinkedIn profile URL (e.g. linkedin.com/in/username).
- "githubUrl": GitHub profile URL (e.g. github.com/username).
- "dribbbleUrl": Dribbble profile URL (e.g. dribbble.com/username).
- "behanceUrl": Behance profile URL (e.g. behance.net/username).
- "twitterUrl": Twitter/X profile URL (e.g. x.com/username or twitter.com/username).
- "instagramUrl": Instagram profile URL (e.g. instagram.com/username).
- "mediumUrl": Medium profile URL (e.g. medium.com/@username).
- "portfolioUrl": actual URL of personal website, portfolio, or homepage (the href). Leave empty if only a label like "PORTFOLIO" is present with no URL.
- "profileEmail": contact email address if clearly stated. Leave empty string if not found.
- Use full URLs (with https://) when possible. Leave any field empty string if not mentioned or if only a label was found.

SKILLS - COMPREHENSIVE EXTRACTION (CRITICAL):
The skills array is used for job matching. Extract ALL skills mentioned ANYWHERE in the resume. Be EXHAUSTIVE.

IMPORTANT: The skills array should be a SUPERSET of all technologies mentioned in individual work experiences. If a technology appears in any role's technologies array, it MUST also appear in the global skills array.

Include ALL of the following categories:
1. TECHNOLOGIES: Angular, TypeScript, React, Node.js, Tailwind, HTML5, SCSS, Ionic, etc.
2. DESIGN TOOLS: Figma, Sketch, Adobe XD, Photoshop, Illustrator, etc.
3. DESIGN PRACTICES: UI/UX Design, Product Design, Web Design, Mobile App Design, Responsive Design, Prototyping, Wireframing, User Flows, Information Architecture, Design Systems, Accessibility, Cross-platform Design, etc.
4. RESEARCH: User Research, Usability Testing, User Testing, A/B Testing, etc.
5. DEVELOPMENT: Front-end Development, Back-end Development, Full-stack Development, Payment Integration, User Authentication, Database Design, SEO, etc.
6. DOMAIN: Web3/Blockchain, PropTech, FinTech, HealthTech, AI/Machine Learning, etc.
7. TESTING: QA Testing, Cross-browser Testing, etc.
8. SOFT SKILLS: Leadership, Team Management, Communication, Stakeholder Management (if demonstrated)

CRITICAL: For experienced professionals (10+ years), the skills array should contain 30-50 unique skills. Go through EVERY work experience and add ALL technologies/skills mentioned to the global skills array. Missing skills means worse job matching.

WORK EXPERIENCE - COMPLETE EXTRACTION:
- Extract ALL positions mentioned, not just the most recent ones
- Include older roles, agency work, freelance clients
- If notable client names are mentioned (Google, World Bank, etc.), add them to the "notableClients" array
- Order by most recent first
- For roles where only company name and title are mentioned without details, still include them with empty arrays for responsibilities/achievements

WORK EXPERIENCE DESCRIPTION - FOLLOW BULLET VS PARAGRAPH RULES:
- For each role, first determine if the resume uses BULLET POINTS or PARAGRAPH format (see BULLET POINTS VS PARAGRAPHS above).
- If BULLETS (explicit markers like •, -, *, 1., 2.): Populate "responsibilities" (and "achievements" when applicable) with ONE array element per bullet. Strip the markers but keep text verbatim. Leave "description" EMPTY.
- If PARAGRAPH (no bullet markers): Put the full text verbatim in "description". Leave "responsibilities" and "achievements" empty.
- If the role has no narrative (only title/company/dates), leave "description" empty and arrays empty.
- Never paraphrase; preserve the candidate's exact wording.
- CRITICAL: Do not put the same content in both description and responsibilities. The presence of bullet markers (•, -, *, etc.) determines the format.

EDUCATION DESCRIPTION - PRESERVE VERBATIM:
- For each education entry, if the resume has any descriptive text (e.g. major, focus area, relevant coursework, thesis, activities), copy it verbatim into "description".
- If the resume has a paragraph, preserve it as-is. If it has bullet points or a short list, preserve them (one per line or as written).
- If there is no narrative for that entry, leave "description" empty.

ACHIEVEMENTS - CONSERVATIVE APPROACH:
For each role, extract achievements that are EXPLICITLY stated or can be DIRECTLY derived from the text:
- Numbers and metrics explicitly mentioned (users, revenue, growth percentages, team sizes)
- Promotions explicitly stated ("Promoted to Lead Designer")
- Specific features/products named as accomplishments
- Scale of impact if explicitly stated (e.g., "used by 1,000+ users")
- Awards or recognition mentioned
- Programs or initiatives explicitly stated as launched

IMPORTANT: 
- Only include achievements that are ACTUALLY in the resume text
- Do NOT fabricate or embellish achievements
- Do NOT convert generic responsibilities into achievements
- If a role has no clear achievements stated, it's acceptable to have an empty or minimal achievements array
- An empty achievements array is better than invented achievements

TECHNOLOGIES PER ROLE - BE THOROUGH:
For each role's technologies array, include ALL of the following that are mentioned for that role:
- Programming languages and frameworks (Angular, TypeScript, React, HTML5, SCSS, Tailwind, etc.)
- Design tools (Figma, Sketch, Adobe XD, etc.)
- Design practices mentioned for that role (Responsive Design, Prototyping, User Research, User Testing, User Flows, etc.)
- Domain technologies (Web3/Blockchain, AI/Machine Learning, etc.)
- Infrastructure (Payment Integration, User Authentication, Database Design, etc.)
- Testing (QA Testing, Usability Testing, etc.)
- Do NOT limit to just the primary tool - capture everything mentioned in the role description

SENIORITY LEVELS:
- 0-2 years: Junior
- 3-5 years: Mid-level  
- 6-9 years: Senior
- 10+ years: Use the HIGHEST title they've actually held:
  - If they held a "Lead" title (e.g., "Lead Product Designer") → seniority = "Lead"
  - If they held a "Principal" or "Staff" title → seniority = "Principal"
  - If they only held "Senior" titles → seniority = "Senior"

IMPORTANT: If someone has 10+ years AND held a Lead title, their seniority MUST be "Lead", not "Senior".

Resume text:
${processedText}`;

      try {
        const completion = await this.resumeAnalyzerService.analyzeResume(prompt);

        // Parse and validate the completion result
        try {
          // Clean up the response (remove markdown code blocks if present)
          let cleanedCompletion = completion.trim();
          if (cleanedCompletion.startsWith("```json")) {
            cleanedCompletion = cleanedCompletion.slice(7);
          }
          if (cleanedCompletion.startsWith("```")) {
            cleanedCompletion = cleanedCompletion.slice(3);
          }
          if (cleanedCompletion.endsWith("```")) {
            cleanedCompletion = cleanedCompletion.slice(0, -3);
          }
          cleanedCompletion = cleanedCompletion.trim();

          const rawProfileData = JSON.parse(cleanedCompletion);
          
          // Validate with Zod schema
          const validationResult = ProfileDataSchema.safeParse(rawProfileData);
          
          if (!validationResult.success) {
            console.warn("Profile data validation warnings:", validationResult.error.errors);
            // Still try to use the data, but with defaults for invalid fields
          }
          
          const profileData = validationResult.success 
            ? validationResult.data 
            : ProfileDataSchema.parse(rawProfileData); // This will apply defaults

          // Convert seniority to array - include lower levels too for job matching flexibility
          let seniorityArray: string[] = [];
          if (profileData.seniority) {
            if (Array.isArray(profileData.seniority)) {
              seniorityArray = profileData.seniority;
            } else {
              // Build a comprehensive seniority array
              // Lead/Principal can also apply to Senior roles
              // Senior can also apply to Mid-level roles if needed
              const seniority = profileData.seniority;
              if (seniority === "Lead" || seniority === "Principal") {
                seniorityArray = [seniority, "Senior"];
              } else if (seniority === "Senior") {
                seniorityArray = ["Senior"];
              } else if (seniority === "Mid-level") {
                seniorityArray = ["Mid-level"];
              } else {
                seniorityArray = [seniority];
              }
            }
          }

          // Filter out any "Founder" roles that might have slipped through
          const filteredTargetRoles = (profileData.targetRoles || []).filter(
            (role: string) => !role.toLowerCase().includes("founder")
          );

          // Merge technologies from work experience into skills array
          // This ensures no skills are missed from individual role technologies
          const allSkills = new Set<string>(profileData.skills || []);
          if (profileData.workExperience && Array.isArray(profileData.workExperience)) {
            for (const exp of profileData.workExperience) {
              if (exp.technologies && Array.isArray(exp.technologies)) {
                for (const tech of exp.technologies) {
                  if (tech && typeof tech === 'string' && tech.trim()) {
                    allSkills.add(tech.trim());
                  }
                }
              }
            }
          }
          // Also add keyTools to skills
          if (profileData.keyTools && Array.isArray(profileData.keyTools)) {
            for (const tool of profileData.keyTools) {
              if (tool && typeof tool === 'string' && tool.trim()) {
                allSkills.add(tool.trim());
              }
            }
          }
          const mergedSkills = Array.from(allSkills);

          // Build the enhanced profile with all available data
          return {
            // Basic fields
            location: profileData.location || "Not Specified",
            seniority: profileData.seniority || "",
            seniorityArray: seniorityArray,
            targetRoles: filteredTargetRoles,
            skills: mergedSkills,
            summary: profileData.summary || "",

            // Enhanced fields
            yearsOfExperience: profileData.yearsOfExperience || "",
            industries: profileData.industries || [],
            educationLevel: profileData.educationLevel || "",
            preferredWorkMode: profileData.preferredWorkMode || "Not Specified",
            keyTools: profileData.keyTools || [],
            languages: profileData.languages || [],

            // Detailed structured information
            workExperience: profileData.workExperience || [],
            education: profileData.education || [],
            certifications: profileData.certifications || [],
            keyProjects: profileData.keyProjects || [],
            portfolioUrl: profileData.portfolioUrl || "",
            notableClients: profileData.notableClients || [],
            linkedinUrl: profileData.linkedinUrl || "",
            githubUrl: profileData.githubUrl || "",
            dribbbleUrl: profileData.dribbbleUrl || "",
            behanceUrl: profileData.behanceUrl || "",
            twitterUrl: profileData.twitterUrl || "",
            instagramUrl: profileData.instagramUrl || "",
            mediumUrl: profileData.mediumUrl || "",
            profileEmail: profileData.profileEmail || "",
          };
        } catch (jsonError) {
          console.error("Error parsing JSON response:", jsonError);
          // Return empty profile data on parse error
          return {
            location: "",
            seniority: "",
            seniorityArray: [],
            targetRoles: [],
            skills: [],
            summary: "",
            workExperience: [],
            education: [],
          };
        }
      } catch (aiError) {
        console.error("Error with AI processing:", aiError);
        throw aiError;
      }
    } catch (error) {
      console.error("Error analyzing resume text:", error);
      throw error;
    }
  }

  /**
   * Move resume from temporary to permanent storage
   */
  moveResumeToPermanentStorage(tempData: {
    tempResumeId: string;
    filePath: string;
    portfolioUrl?: string;
    extractedText: string;
    originalFileName?: string;
  }): Observable<{
    success: boolean;
    resumeStored: boolean;
    publicUrl?: string;
  }> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(
        () => new Error("User must be authenticated to manage resume")
      );
    }

    // Extract original filename from tempData.filePath if not provided
    const originalFileName = tempData.originalFileName || 
      decodeURIComponent(tempData.filePath.split('/').pop() || 'Resume.pdf');
    
    const newFilePath = `permanent/${userId}/${Date.now()}-${originalFileName}`;

    return from(
      this.supabase
        .getClient()
        .storage.from("resumes")
        .download(tempData.filePath)
    ).pipe(
      switchMap((downloadResult) => {
        if (downloadResult.error) {
          console.error("Error downloading temp file:", downloadResult.error);
          return this.updateUserRecordWithoutResumeUrl(
            userId,
            tempData.extractedText
          ).pipe(
            map(() => ({
              success: true,
              resumeStored: false,
              publicUrl: undefined,
            }))
          );
        }

        return from(
          this.supabase
            .getClient()
            .storage.from("resumes")
            .upload(newFilePath, downloadResult.data, {
              contentType: "application/pdf",
              upsert: true,
            })
        ).pipe(
          switchMap((uploadResult) => {
            if (uploadResult.error) {
              console.warn(
                "Upload to permanent storage failed, updating DB without URL:",
                uploadResult.error
              );
              return this.updateUserRecordWithoutResumeUrl(
                userId,
                tempData.extractedText
              ).pipe(
                map(() => ({
                  success: true,
                  resumeStored: false,
                  publicUrl: undefined,
                }))
              );
            }

            const publicUrlResult = this.supabase
              .getClient()
              .storage.from("resumes")
              .getPublicUrl(newFilePath);
            const publicUrl = publicUrlResult.data.publicUrl;

            return this.updateUserRecordWithResumeUrl(
              userId,
              publicUrl,
              tempData.extractedText,
              originalFileName
            ).pipe(
              map(() => ({
                success: true,
                resumeStored: true,
                publicUrl: publicUrl,
              }))
            );
          })
        );
      }),
      catchError((error) => {
        console.error("Error in resume move/storage process:", error);
        return throwError(() => ({
          message:
            "Failed to save resume file permanently. Profile details might still be saved.",
          details: error.message || error,
        }));
      })
    );
  }

  // Helper function to update user record with resume URL
  private updateUserRecordWithResumeUrl(
    userId: string,
    resumeUrl: string,
    resumeText: string,
    originalFileName?: string
  ): Observable<any> {
    return from(
      this.supabase
        .getClient()
        .from("users")
        .upsert(
          {
            id: userId,
            resume_url: resumeUrl,
            resume_text: resumeText,
            resume_original_filename: originalFileName,
            updated_at: new Date().toISOString(),
            email: this.authService.getCurrentUser()?.email || "",
          },
          { onConflict: "id" }
        )
    ).pipe(
      map((res) => {
        if (res.error) throw res.error;
        return res;
      })
    );
  }

  // Helper function to update user record without resume URL (text only)
  private updateUserRecordWithoutResumeUrl(
    userId: string,
    resumeText: string
  ): Observable<any> {
    return from(
      this.supabase
        .getClient()
        .from("users")
        .upsert(
          {
            id: userId,
            resume_text: resumeText,
            resume_url: null,
            updated_at: new Date().toISOString(),
            email: this.authService.getCurrentUser()?.email || "",
          },
          { onConflict: "id" }
        )
    ).pipe(
      map((res) => {
        if (res.error) throw res.error;
        return res;
      })
    );
  }

  /**
   * Save profile details
   */
  saveProfileDetails(
    details: ProfileDetails,
    extractedText?: string,
    resumeFilePath?: string
  ): Observable<{ success: boolean }> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      throw new Error("User must be authenticated to save profile");
    }

    const parsedExperienceYears = this.parseExperienceYears(details.yearsOfExperience);
    const derivedJobTitle = details.targetRoles?.[0] || 
                            details.workExperience?.[0]?.title || 
                            null;

    const userRecordUpdate: any = {
      id: userId,
      location: details.location,
      resume_text: extractedText,
      resume_url: resumeFilePath,
      updated_at: new Date().toISOString(),
      email: this.authService.getCurrentUser()?.email || "",
      website: details.portfolioUrl?.trim() || undefined,

      // Direct mapping to top-level columns based on schema
      education: details.education || [],
      work_history: details.workExperience || [],
      skills: details.skills || [],
      profile_summary: details.summary || "",
      certifications: details.certifications || [],
      experience_years: parsedExperienceYears,
      job_title: derivedJobTitle,
    };

    const preferences: any = {
      seniority: details.seniority,
      targetRoles: details.targetRoles || [],
      yearsOfExperience: details.yearsOfExperience || "",
      industries: details.industries || [],
      preferredWorkMode: details.preferredWorkMode || "Not Specified",
    };

    const detailedProfileForPrefs: any = {};
    if (details.keyProjects && details.keyProjects.length > 0) {
      detailedProfileForPrefs.keyProjects = details.keyProjects;
    }
    if (details.languages && details.languages.length > 0) {
      detailedProfileForPrefs.languages = details.languages;
    }
    if (details.keyTools && details.keyTools.length > 0) {
      detailedProfileForPrefs.keyTools = details.keyTools;
    }
    if (details.educationLevel) {
      detailedProfileForPrefs.educationLevel = details.educationLevel;
    }
    if (details.notableClients && details.notableClients.length > 0) {
      detailedProfileForPrefs.notableClients = details.notableClients;
    }
    if (details.portfolioUrl) {
      detailedProfileForPrefs.portfolioUrl = details.portfolioUrl;
    }

    if (Object.keys(detailedProfileForPrefs).length > 0) {
      preferences.detailedProfile = detailedProfileForPrefs;
    }

    userRecordUpdate.preferences = preferences;

    // Merge extracted profile links (LinkedIn, GitHub, portfolio) with existing
    return from(
      this.supabase
        .getClient()
        .from("users")
        .select("profile_links")
        .eq("id", userId)
        .single()
    ).pipe(
      map((res) => (res.data?.profile_links as Record<string, string>) || {}),
      switchMap((existingLinks) => {
        const merged: Record<string, string | undefined> = {
          ...existingLinks,
          email:
            details.profileEmail?.trim() ||
            existingLinks?.["email"] ||
            undefined,
          portfolio_website:
            details.portfolioUrl?.trim() ||
            existingLinks?.["portfolio_website"] ||
            undefined,
          linkedin:
            details.linkedinUrl?.trim() ||
            existingLinks?.["linkedin"] ||
            undefined,
          github:
            details.githubUrl?.trim() ||
            existingLinks?.["github"] ||
            undefined,
          dribbble:
            details.dribbbleUrl?.trim() ||
            existingLinks?.["dribbble"] ||
            undefined,
          behance:
            details.behanceUrl?.trim() ||
            existingLinks?.["behance"] ||
            undefined,
          twitter:
            details.twitterUrl?.trim() ||
            existingLinks?.["twitter"] ||
            undefined,
          instagram:
            details.instagramUrl?.trim() ||
            existingLinks?.["instagram"] ||
            undefined,
          medium:
            details.mediumUrl?.trim() ||
            existingLinks?.["medium"] ||
            undefined,
        };
        userRecordUpdate.profile_links = merged;
        return from(
          this.supabase.getClient().from("users").upsert(userRecordUpdate, {
            onConflict: "id",
          })
        );
      }),
      map((response) => {
        if (response.error) {
          throw response.error;
        }
        return { success: true };
      }),
      catchError((error) => {
        return throwError(() => new Error("Failed to save profile details."));
      })
    );
  }

  /**
   * Get profile details
   */
  getProfileDetails(): Observable<ProfileDetails | null> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return of(null);
    }

    return from(
      this.supabase.getClient().from("users").select().eq("id", userId).single()
    ).pipe(
      map((response) => {
        if (response.error) throw response.error;
        if (!response.data) return null;

        const preferences = response.data.preferences || {};
        const detailedProfile = preferences.detailedProfile || {};

        return {
          userId: userId,
          // Basic information
          location: response.data.location || "",
          seniority: preferences.seniority || "",
          targetRoles: preferences.targetRoles || [],

          // Resume details
          extractedText: response.data.resume_text,
          resumeFilePath: response.data.resume_url,
          resumeOriginalFilename: response.data.resume_original_filename,
          hasResumeData: !!(response.data.resume_text && response.data.resume_text.length > 100),

          // Enhanced information
          skills: response.data.skills || preferences.skills || [],
          summary: response.data.profile_summary || detailedProfile.summary || "",
          yearsOfExperience: response.data.experience_years || preferences.yearsOfExperience || "",
          industries: preferences.industries || [],
          educationLevel: response.data.education?.[0]?.degree || detailedProfile.education?.[0]?.degree || "",
          preferredWorkMode: preferences.preferredWorkMode || "Not Specified",
          keyTools: detailedProfile.keyTools || [],
          languages: detailedProfile.languages || [],
          portfolioUrl: detailedProfile.portfolioUrl || "",
          notableClients: detailedProfile.notableClients || [],

          // Detailed structured information
          workExperience: response.data.work_history || detailedProfile.workExperience || [],
          education: response.data.education || detailedProfile.education || [],
          certifications: response.data.certifications || detailedProfile.certifications || [],
          keyProjects: detailedProfile.keyProjects || [],
        };
      })
    );
  }

  /**
   * Helper to get headers with Authorization token if available
   */
  private getAuthHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    const token = this.authService.getAccessToken();
    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  }

  /**
   * Parse experience years from various formats
   * Handles: "12", "12+", "12 years", "over 12 years", etc.
   */
  private parseExperienceYears(value: string | number | undefined): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    
    if (typeof value === 'number') {
      return Math.round(value);
    }
    
    const numMatch = value.toString().match(/(\d+)/);
    if (numMatch) {
      return parseInt(numMatch[1], 10);
    }
    
    return null;
  }

  /**
   * Extract text and analyze resume after authentication
   */
  analyzeResume(tempData: {
    tempResumeId: string;
    filePath: string;
    portfolioUrl?: string;
  }): Observable<{
    tempResumeId: string;
    suggestedProfile?: ProfileDetails;
    filePath: string;
    portfolioUrl?: string;
    extractedText: string;
  }> {
    return from(this.extractTextFromPDF(tempData.filePath)).pipe(
      switchMap((extractedText) =>
        from(this.analyzeResumeText(extractedText)).pipe(
          catchError((error) => {
            console.error("Resume analysis failed:", error);
            return of({
              location: "",
              seniority: "",
              targetRoles: [],
            });
          }),
          map((suggestedProfile) => ({
            tempResumeId: tempData.tempResumeId,
            suggestedProfile,
            filePath: tempData.filePath,
            portfolioUrl: tempData.portfolioUrl,
            extractedText,
          }))
        )
      ),
      catchError((error) => {
        console.error("Analysis process failed:", error);
        return throwError(() => ({
          message:
            error.message || "Failed to analyze resume. Please try again.",
          details: error,
        }));
      })
    );
  }
}